/**
 * Применение карточки Популярных («инструмента») — модульные хелперы.
 *
 * Единая логика для сетки/демо-блока PopularModelsTab И поиска по вкладкам
 * на страницах BotsImages/BotsVideo (клик по результату = клик по карточке).
 * Вынесено из компонента: react-refresh требует, чтобы файл компонента
 * экспортировал только компоненты.
 */
import type { CardPhotoSlot } from '../../../photo-slots'
import { brandModelToModel, type Brand } from '../../../hooks/useProviders.ts'
import { attachFromPopularCard } from '../../../api/attachedStyleCard.ts'
import { cardPrimaryUrl } from './cardSeo.ts'
import { getModelConfig } from '../../generate/utils/generateModelConfig.ts'
import { activeDiscount, trendDiscount, type TrendPipePricing } from '../../../utils/discount'
import type { Model } from '../../../data/models.ts'
import type { CardKind, GenerationCard, GenerationCardSettings, TrendMemberPreview } from '../../../types/generationCard.ts'
import { getAttachedStyleCard } from '../../../api/attachedStyleCard.ts'

export interface BackendCard {
	id: string
	title: string
	description?: string
	background_url?: string
	/** Производный URL webp-превью фона (бэк, public_page_sections._enrich) */
	background_preview_url?: string | null
	target_adapter?: string
	target_model?: string
	system_prompt?: string
	/** Гость: бэк вырезал промпт и выставил флаг (public_page_sections). */
	prompt_locked?: boolean
	target_pages?: string[]
	hidden_pages?: string[]
	settings?: GenerationCardSettings
	used_count?: number
	/** Видео-превью (video) / аудио (audio) карточки — играет поверх постера на сайте. */
	video_url?: string
	/** Постер видео = его первый кадр (`<key>_preview.webp`, кладёт upload-video,
	 *  та же конвенция, что у превью обложек). Нужен карточкам БЕЗ обложки:
	 *  без него плитка держалась на том, что браузер сам решит декодировать
	 *  кадр — часть устройств этого не делает. Едет атрибутом poster, поэтому
	 *  отсутствующий файл (бэкфил не прогнан) плитку сломать не может: будет
	 *  ровно прежнее поведение. */
	video_preview_url?: string | null
	/** Лёгкий ролик-сосед для автоплея плитки (бэк отдаёт, только если объект есть). */
	video_loop_url?: string | null
	audio_url?: string
	/** Наглядное демо (image-карточки). demo_type: 'ba' — слайдер «до/после»,
	    'video' — демо-видео (video_url + постер=баннер). */
	demo_type?: 'ba' | 'video'
	/** Формат демо-блока (aspect-ratio, напр. '9:16'); пусто = авто по settings.aspectRatio */
	demo_aspect?: string
	/** Размеры ролика/обложки (из ответа аплоада, старым — бэкфилл) — форма плитки
	 *  комбинированной ленты; tile_format — форма, заданная админом (reels|wide|square). */
	video_w?: number | null
	video_h?: number | null
	cover_w?: number | null
	cover_h?: number | null
	tile_format?: string | null
	/** Плашка «Это ты» на плитке ленты — галочка карточки в админке. */
	you_badge?: boolean
	/** Зоны страницы трендов (video | photo), явная отметка админа; нет поля — подсказка по медиа (utils/trendZones). */
	trend_zones?: string[]
	demo_before_url?: string
	/** Пара «до/после» БЕЗ вариаций (владелец 2026-08-16): просто наглядный
	 *  слайдер — селекта стилей в виджете нет. Вариации, если есть, главнее. */
	demo_after_url?: string
	demo_variants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; description?: string; is_open?: boolean; placeholder?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
	/** Мультистиль: тип карточки + карточное «поле юзера» поверх выбора стиля. */
	card_kind?: CardKind
	/** Мульти-тренд (22.10): id участников (первый — главный) и сами участники — бэк кладёт их внутрь группы
	 *  (обогащённые, как карточки пула) и прячет из пула страницы. Цена/зона плитки группы — поля главного. */
	trend_ids?: string[]
	members?: BackendCard[]
	/** Карточка из шаблона флоу (card_kind='flow'): клик открывает шаблон на холсте
	 *  (/flows/t/{id}); `flow` — сводка шаблона с бэка (обложка, шаги, цена «от»). */
	flow_template_id?: string
	flow?: FlowCardSummary
	user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
	/** Именованные фото-слоты юзера (подписи ячеек, фото уходят по порядку). */
	photo_slots?: CardPhotoSlot[] | null
	/** Виджет карточки (владелец 2026-08-16): скрыть «Персонажа» (инструментам
	 *  он часто не нужен). */
	hide_character?: boolean
	/** Лимит фото юзера в обычном мульти-входе (1..N, кламп по модели). */
	user_photo_limit?: number
	/** Подпись под слотом «Ваше фото» (как hint у именованных слотов). */
	user_photo_hint?: string
	hide_end_image?: boolean
	end_image_hint?: string
	character_hint?: string
	/** Медиа-референсы «под капотом» + локи полей юзера (админ, video-модели). */
	reference_image_url?: string
	user_image_locked?: boolean
	has_reference_video?: boolean
	/** Секунды видео тренда (замер сервера; есть, когда включена плата за секунды входного видео) — цена плитки. */
	reference_video_seconds?: number | null
	has_reference_audio?: boolean
	reference_video_url?: string
	user_video_locked?: boolean
	reference_audio_url?: string
	user_audio_locked?: boolean
	/** «Своя цена карточки»: floor-оверрайд в кредитах (админ, ≥ базовой). */
	price_override?: number
	/** Пайплайн-карточка (тренды «фото → видео»): slug шаблона пайплайна —
	 *  GTM ведёт многоэтапный режим вместо одиночной генерации. */
	pipeline_slug?: string
	/** Этапы тренда: цены/акция для витрины (движок этапов — шаблон бэка). */
	pipeline?: TrendPipePricing
	/** Акция: скидочная цена + срок (ISO UTC). Пока не истёк — списывается она. */
	discount_price?: number
	discount_until?: string
	/** Категория инструментов НА КАЖДОЙ странице (page → slug, пеблы). */
	category_by_page?: Record<string, string>
	/** «Только в категориях»: на страницах с категорией скрыта из «Все». */
	hide_from_all?: boolean
	/** ⭐ пебл «Популярные» таба «Инструменты». */
	is_popular?: boolean
	/** Доп-стиль плитки: 'droplet' — «стекло-капля» (рефракция кромки + тень).
	 *  Точечный премиальный акцент, задаёт админ; пусто — обычная плитка. */
	tile_style?: string
	slug?: string
	seo_title?: string
	seo_description?: string
	seo_h1?: string
}

/** Тип карточки после нормализации BackendCard → item (см. rawCardToItem). */
/** Сводка шаблона флоу у карточки-источника (бэк: page-sections/public, поле flow). */
export type FlowCardSummary = {
	id: string
	title: string
	cover_url: string | null
	cover_position?: string | null
	steps: number
	price_from: number | null
}

export type PopularCardItem = {
	id: string
	title: string
	description: string
	backgroundImageUrl: string
	backgroundPreviewUrl?: string | null
	modelLogoUrl: string
	creditsLabel: string
	rating: number
	reviews: number
	targetAdapter: string
	targetModelId: string
	defaultPrompt: string
	settings?: GenerationCardSettings
	usedCount?: number
	videoUrl?: string
	/** Постер видео (первый кадр) — атрибут poster, см. BackendCard. */
	videoPosterUrl?: string
	/** Лёгкий беззвучный ролик (<key>_loop.mp4) — его играет плитка при автоплее;
	 *  нет соседа (бэкфилл не прогнан) — играем оригинал. */
	videoLoopUrl?: string
	audioUrl?: string
	disabled: boolean
	demoType?: 'ba' | 'video'
	demoBeforeUrl?: string
	demoAfterUrl?: string
	demoVariants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
	cardKind?: CardKind
	/** Мульти-тренд: участники для плитки (бейдж, свотчи) и полные карточки участников — из них применяется
	 *  выбранный (см. applyPopularItem). */
	trendMembers?: TrendMemberPreview[]
	trendMemberCards?: BackendCard[]
	/** Участник, применённый ИЗ группы: пин носит группу, виджет рисует ряд участников. */
	trendGroup?: TrendGroupRef
	/** Карточка-флоу: шаблон, число шагов и цена «от» для ленты плитки. */
	flowTemplateId?: string
	flowSteps?: number
	flowPriceFrom?: number | null
	flowCoverPosition?: string | null
	userInput?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
	photoSlots?: CardPhotoSlot[] | null
	hideCharacter?: boolean
	userPhotoLimit?: number
	userPhotoHint?: string
	hideEndImage?: boolean
	endImageHint?: string
	characterHint?: string
	/** Гость: готовый промпт скрыт бэком → виджет покажет CTA (см. attachedStyleCard). */
	promptLocked?: boolean
	/** Фото «под капотом» от админа + скрытие пользовательского слота под него. */
	referenceImageUrl?: string
	userImageLocked?: boolean
	hasRefVideo?: boolean
	/** Длина видео тренда по замеру сервера (reference_video_seconds) — в пин: цена гостю как на плитке. */
	refVideoSeconds?: number | null
	hasRefAudio?: boolean
	referenceVideoUrl?: string
	userVideoLocked?: boolean
	referenceAudioUrl?: string
	userAudioLocked?: boolean
	priceOverride?: number
	discountPercent?: number
	discountLeftLabel?: string
	/** Конец акции (ISO) — едет в пин: живость промо проверяется на расчёте. */
	discountUntil?: string
	/** Обычная цена ДО акции — зачёркнутый ценник промо-стикера плитки
	 *  и гейт промо в виджете (см. GenerationCard.regularPrice). */
	creditsOldLabel?: string
	regularPrice?: number
	pipelineSlug?: string
	/** 'droplet' — плитка в «стекле-капле» (см. DropletGlass). */
	tileStyle?: string
	slug?: string
	primaryUrl?: string | null
}

/** Группа мульти-тренда, из которой применён участник: виджет рисует ряд участников, а переключение ведёт
 *  на лендинг группы с выбранным участником (и с /generate тоже — там применять некому). */
export interface TrendGroupRef {
	id: string
	title: string
	path?: string
	members: TrendMemberPreview[]
}

/** Участники группы: только карточки с id (бэк отдаёт их обогащёнными). У обычной карточки — пусто.
 *  Группа со своей нейросетью (23.10, «тренд остаётся») — сама главный: бэк кладёт её первой (id = id группы),
 *  дальше версии. */
export function trendMembersOf(card: Pick<BackendCard, 'card_kind' | 'members'>): BackendCard[] {
	return card.card_kind === 'multitrend' ? (card.members ?? []).filter((m) => !!m?.id) : []
}

export function trendMemberPreview(card: BackendCard): TrendMemberPreview {
	return {
		id: card.id,
		title: card.title || '',
		previewUrl: card.background_preview_url || card.background_url || card.video_preview_url || undefined,
		videoUrl: card.video_url || undefined,
		videoLoopUrl: card.video_loop_url || undefined,
		videoPosterUrl: card.video_preview_url || undefined,
	}
}

/** Поля группы для item плитки/применения; у обычной карточки — пусто (ключей нет). */
export function trendGroupFields(card: BackendCard): Pick<PopularCardItem, 'trendMembers' | 'trendMemberCards'> {
	const members = trendMembersOf(card)
	if (!members.length) return {}
	return { trendMembers: members.map(trendMemberPreview), trendMemberCards: members }
}

/** Группа, в которой спрятан тренд с этим слагом (старая ссылка на участника): открываем группу с ним. */
export function findTrendMemberHome(cards: BackendCard[], slug: string): { group: BackendCard; memberId: string } | null {
	const norm = slug.toLowerCase()
	for (const g of cards) {
		// Сама группа в своём ряду — не спрятанный участник: её находят как карточку.
		const m = trendMembersOf(g).find((x) => x.id !== g.id && (x.slug || '').toLowerCase() === norm)
		if (m) return { group: g, memberId: m.id }
	}
	return null
}

/** Участники групп для поиска по витрине: в пуле они спрятаны, находка ведёт на лендинг группы с этим участником. */
export function trendMemberSearchHits(pool: BackendCard[]): Array<{ id: string; title: string; previewUrl?: string; group: BackendCard }> {
	const out: Array<{ id: string; title: string; previewUrl?: string; group: BackendCard }> = []
	for (const g of pool) {
		for (const m of trendMembersOf(g)) {
			if (m.id === g.id) continue // сама группа в пуле видна — второй находки не нужно
			out.push({ id: m.id, title: m.title || 'Без названия', previewUrl: m.background_preview_url || m.background_url || m.video_preview_url || undefined, group: g })
		}
	}
	return out
}

/** Какого участника группы открыть: явно выбранный (клик по свотчу) → тот, что уже в пине этой группы (F5,
 *  возврат на лендинг) → главный (первый). null — участников нет, группу показывать нечем. */
export function pickTrendMember(
	group: Pick<PopularCardItem, 'id' | 'trendMemberCards'>,
	wantedId?: string | null,
	pinned?: { id: string | null; trendGroup?: { id: string } } | null,
): BackendCard | null {
	const cards = group.trendMemberCards ?? []
	if (!cards.length) return null
	const wanted = wantedId ? cards.find((c) => c.id === wantedId) : undefined
	if (wanted) return wanted
	const pinnedId = pinned?.trendGroup?.id === group.id ? pinned.id : null
	return (pinnedId && cards.find((c) => c.id === pinnedId)) || cards[0]
}

/** Эффективная обложка карточки (владелец 2026-08-16): у мультистиля
 *  обложкой служит ПЕРВЫЙ стиль — отдельный баннер не обязателен (у каждого
 *  стиля своя картинка). Явный баннер, если задан, остаётся приоритетным. */
export function effectiveCardCover(card: Pick<BackendCard, 'background_url' | 'card_kind' | 'demo_variants'>): string {
	if (card.background_url) return card.background_url
	if (card.card_kind === 'multistyle') {
		const first = (card.demo_variants || []).find(v => !v.is_open && v.after_url)
		if (first) return first.after_url
	}
	return ''
}

/** BackendCard → PopularCardItem. Акция: эффективная цена подменяет
 *  priceOverride (= то, что спишет бэк по своей UTC-проверке). */
export function rawCardToItem(raw: BackendCard): PopularCardItem {
	// Тренд: акция задаётся в этапах (−N% на цены обоих шагов) — плашка/цена
	// из них; обычная карточка — из price_override/discount_price. Показ цены
	// по pipeline.enabled, НЕ по слагу (слаг — для запуска, не для цены).
	const discount = raw.pipeline?.enabled
		? trendDiscount(raw.pipeline)
		: activeDiscount(raw.price_override, raw.discount_price, raw.discount_until)
	return {
		id: raw.id,
		title: raw.title || '',
		description: raw.description || '',
		backgroundImageUrl: effectiveCardCover(raw),
		backgroundPreviewUrl: raw.background_preview_url || null,
		tileStyle: raw.tile_style || undefined,
		modelLogoUrl: '',
		creditsLabel: '',
		rating: 0,
		reviews: 0,
		targetAdapter: raw.target_adapter || '',
		targetModelId: raw.target_model || '',
		defaultPrompt: raw.system_prompt || '',
		settings: raw.settings,
		usedCount: raw.used_count,
		videoUrl: raw.video_url,
		videoPosterUrl: raw.video_preview_url || undefined,
		videoLoopUrl: raw.video_loop_url || undefined,
		audioUrl: raw.audio_url,
		// Карточка-флоу без модели — норма: у неё вместо генерации переход в шаблон; у группы трендов — участник.
		disabled: !raw.target_adapter && !raw.pipeline_slug && raw.card_kind !== 'flow' && raw.card_kind !== 'multitrend',
		demoType: raw.demo_type,
		demoBeforeUrl: raw.demo_before_url,
		demoAfterUrl: raw.demo_after_url,
		demoVariants: raw.demo_variants,
		cardKind: raw.card_kind,
		...trendGroupFields(raw),
		flowTemplateId: raw.flow_template_id,
		flowSteps: raw.flow?.steps,
		flowPriceFrom: raw.flow?.price_from,
		flowCoverPosition: raw.flow?.cover_position ?? null,
		userInput: raw.user_input,
		photoSlots: raw.photo_slots,
		hideCharacter: raw.hide_character,
		userPhotoLimit: raw.user_photo_limit,
		userPhotoHint: raw.user_photo_hint,
		hideEndImage: raw.hide_end_image,
		endImageHint: raw.end_image_hint,
		characterHint: raw.character_hint,
		promptLocked: raw.prompt_locked,
		referenceImageUrl: raw.reference_image_url,
		userImageLocked: raw.user_image_locked,
		referenceVideoUrl: raw.reference_video_url,
		hasRefVideo: raw.has_reference_video,
		refVideoSeconds: raw.reference_video_seconds,
		hasRefAudio: raw.has_reference_audio,
		userVideoLocked: raw.user_video_locked,
		referenceAudioUrl: raw.reference_audio_url,
		userAudioLocked: raw.user_audio_locked,
		priceOverride: discount?.price ?? raw.price_override,
		discountPercent: discount?.percent,
		discountLeftLabel: discount?.leftLabel,
		discountUntil: discount?.until,
		regularPrice: discount ? raw.price_override : undefined,
		pipelineSlug: raw.pipeline_slug,
		slug: raw.slug,
		primaryUrl: cardPrimaryUrl(raw),
	}
}

/** Зависимости применения (хуки вызывающей стороны). */
export interface ApplyPopularDeps {
	category: 'image' | 'video' | 'audio' | 'trends' | 'photostudio'
	findModel: (adapter: string, modelId: string) => Model | null
	handleStyleApply: (card: GenerationCard) => void
	/** SelectedModelContext.selectModel — PopularCardItem структурно совместим. */
	selectModel: (item: PopularCardItem & { creditsLabel: string }) => void
	navigate: (path: string) => void
	/** Мульти-тренд: какого участника открыть (клик по свотчу в виджете); пусто — из пина или главного. */
	trendMemberId?: string | null
	/** Адрес лендинга группы (с контекстом витрины) — виджет возвращается на него при переключении участника. */
	trendGroupPath?: string
	/** Внутреннее: смена участника той же группы с тем же числом полей — фото пользователя остаются. */
	keepUploads?: boolean
}

/** Хелпер findModel по брендам провайдеров (та же логика, что была в табе). */
export function makeFindModel(brands: Brand[]) {
	return (adapter: string, modelId: string): Model | null => {
		for (const brand of brands) {
			for (const bm of brand.models) {
				if (bm.adapter === adapter && bm.id === modelId) {
					return brandModelToModel(brand, bm)
				}
			}
		}
		return null
	}
}

/** Одинаковая «форма загрузок» у двух карточек группы: число именованных полей, лимит фото юзера, число фото этапов
 *  тренда «фото → видео», наличие админского фото-референса и лимит фото у нейросети. Только при ней фото пользователя
 *  переживают смену версии (keepUploads): иначе лишние фото уехали бы в запрос, а референс новой карточки не встал бы. */
export function sameUploadShape(a: BackendCard, b: BackendCard, findModel: ApplyPopularDeps['findModel']): boolean {
	if ((a.photo_slots?.length ?? 0) !== (b.photo_slots?.length ?? 0)) return false
	if ((a.user_photo_limit ?? 0) !== (b.user_photo_limit ?? 0)) return false
	const pipePhotos = (c: BackendCard) =>
		c.pipeline?.enabled ? ((c.pipeline as unknown as { photos_count?: number }).photos_count ?? 1) : 0
	if (pipePhotos(a) !== pipePhotos(b)) return false
	if (!!a.reference_image_url !== !!b.reference_image_url) return false
	const maxImages = (c: BackendCard) =>
		c.target_adapter && c.target_model ? (findModel(c.target_adapter, c.target_model)?.maxImages ?? 1) : 1
	return maxImages(a) === maxImages(b)
}

/** Применение карточки к виджету (пин + модель + промпт + настройки). */
export function applyPopularItem(item: PopularCardItem, initialKey: string | undefined, deps: ApplyPopularDeps) {
	const { category, findModel, handleStyleApply, selectModel, navigate } = deps
	// Мульти-тренд (22.10): в виджет идёт УЧАСТНИК группы (его промпт, референсы, цена, source_card_id), а группа
	// едет в пин — ряд участников и возврат на лендинг группы. Группа со своей нейросетью (23.10) — сама первый
	// участник (бэк кладёт её карточку первой, без вида «группа»), папка без неё применяется первой версией.
	if (item.cardKind === 'multitrend') {
		const pin = getAttachedStyleCard()
		const member = pickTrendMember(item, deps.trendMemberId, pin)
		if (!member) return
		// Одно фото на все образы (23.10): переключаемся между участниками ЭТОЙ группы и форма загрузок у них одна
		// (поля, лимит фото, этапы, референс, лимит модели) — фото пользователя не сбрасываем; референсы карточки
		// «под капотом» подставятся заново, запертые у новой карточки каналы чистятся целиком (GenerateContext.resetCardRefs).
		const prev = pin && pin.trendGroup?.id === item.id && pin.id && pin.id !== member.id
			? (item.trendMemberCards ?? []).find((c) => c.id === pin.id)
			: undefined
		const keepUploads = !!prev && sameUploadShape(prev, member, findModel)
		applyPopularItem(
			{
				...rawCardToItem(member),
				trendGroup: {
					id: item.id,
					title: item.title,
					path: deps.trendGroupPath ?? item.primaryUrl ?? undefined,
					members: item.trendMembers ?? [],
				},
			},
			initialKey,
			{ ...deps, keepUploads },
		)
		return
	}
	// Карточка-флоу: ничего не подставляем в виджет — открываем шаблон на холсте
	// (ленивая копия появится у юзера при первом действии). Без шаблона клик пустой.
	if (item.cardKind === 'flow') {
		if (item.flowTemplateId) navigate(`/flows/t/${item.flowTemplateId}`)
		return
	}
	// Пин «Выбранный стиль» у виджета + атрибуция генераций — единый канал
	// (маппинг полей живёт в конвертере, см. api/attachedStyleCard).
	attachFromPopularCard(item, {
		toolMode:
			category === 'image' ||
			category === 'video' ||
			category === 'trends' ||
			category === 'photostudio' ||
			!!item.pipelineSlug,
		activeVariantKey: initialKey,
		keepUploads: deps.keepUploads || undefined,
	})
	// Если есть реальная модель — открываем inline в /bots/* виджете
	// и передаём промпт/настройки через GenerationCard → applyCard.
	const realModel =
		item.targetAdapter && item.targetModelId
			? findModel(item.targetAdapter, item.targetModelId)
			: null
	if (realModel) {
		// type='style' триггерит setShowStyleHint в applyCard logic, когда
		// модель принимает изображение на вход — это показывает pill
		// "Добавьте фото которое хотите преобразовать". Для чисто текстовых
		// моделей оставляем 'generation' чтобы pill не показывался.
		const showImageUpload = getModelConfig(realModel).showImageUpload
		const card: GenerationCard = {
			id: `popular-${item.id}`,
			type: showImageUpload ? 'style' : 'generation',
			previewUrl: item.backgroundImageUrl || '',
			// Гость (promptLocked): промпт пустой — под блюр-оверлеем нечему
			// просвечивать (иначе тайтл/описание бликуют сквозь стекло).
			prompt: item.promptLocked
				? ''
				: item.defaultPrompt || `${item.title}${item.description ? '\n\n' + item.description : ''}`,
			modelId: realModel.id,
			modelName: realModel.name,
			isVideo: realModel.category === 'video',
			kind: realModel.category === 'audio' ? 'audio' : realModel.category === 'video' ? 'video' : 'image',
			settings: item.settings,
			// Демо-переменные: чипы карточки + с какой вариации стартовать.
			demoVariants: item.demoVariants,
			demo_after_url: item.demoAfterUrl,
			card_kind: item.cardKind,
			user_input: item.userInput ?? undefined,
			photo_slots: item.photoSlots ?? undefined,
			hide_character: item.hideCharacter,
			user_photo_limit: item.userPhotoLimit,
			user_photo_hint: item.userPhotoHint,
			hide_end_image: item.hideEndImage,
			end_image_hint: item.endImageHint,
			character_hint: item.characterHint,
			activeVariantKey: initialKey,
			keepUploads: deps.keepUploads || undefined,
			// Все «Популярные» на images/video/trends/фотостудии → фокус-режим
			// инструмента.
			toolMode:
				category === 'image' || category === 'video' || category === 'trends' || category === 'photostudio',
			// «Своя цена карточки»: floor-оверрайд едет в пин через applyCard-канал.
			priceOverride: item.priceOverride,
		}
		handleStyleApply(card)
		return
	}
	// Пайплайн-карточка (тренд «фото → видео»): target-модель не нужна вовсе —
	// открываем МЕСТНЫЙ виджет /bots тем же inline-флоу, что у карточек с
	// моделью (пин уже стоит, GTM включается от toolMode; слоты фото и цены —
	// от этапов шаблона). Никакого увода на /generate.
	if (item.pipelineSlug) {
		handleStyleApply({
			id: `popular-${item.id}`,
			type: 'generation',
			previewUrl: item.backgroundImageUrl || '',
			prompt: '',
			modelId: '',
			modelName: '',
			isVideo: category === 'video',
			toolMode: true,
			demoVariants: item.demoVariants,
			demo_after_url: item.demoAfterUrl,
			card_kind: item.cardKind,
			user_input: item.userInput ?? undefined,
			photo_slots: item.photoSlots ?? undefined,
			hide_character: item.hideCharacter,
			user_photo_limit: item.userPhotoLimit,
			user_photo_hint: item.userPhotoHint,
			hide_end_image: item.hideEndImage,
			end_image_hint: item.endImageHint,
			character_hint: item.characterHint,
			activeVariantKey: initialKey,
			keepUploads: deps.keepUploads || undefined,
			priceOverride: item.priceOverride,
		})
		return
	}
	// Fallback для карточек без target-модели (badge 'Скоро'): просто
	// уходим на /generate — там юзер сам выберет модель. PopularCardItem
	// структурно совместим с PopularModelCard — каст не нужен.
	selectModel(item)
	navigate('/generate')
}
